import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Camera,
  QrCode,
  Barcode,
  Search,
  X,
  AlertCircle,
  CheckCircle,
  Loader2,
  SwitchCamera,
  Flashlight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';

// Note: In production, you'd use a library like @yudiel/react-qr-scanner or html5-qrcode
// This is a demonstration implementation

interface ScannedAsset {
  id: string;
  name: string;
  assetTag: string;
  serialNumber?: string;
  status: string;
  category?: { name: string };
  brand?: { name: string };
  assignedTo?: { firstName: string; lastName: string };
}

type ScanMode = 'qr' | 'barcode' | 'manual';

export default function ScannerPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  const [scanMode, setScanMode] = useState<ScanMode>('qr');
  const [cameraActive, setCameraActive] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [scannedAsset, setScannedAsset] = useState<ScannedAsset | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [flashOn, setFlashOn] = useState(false);

  // Start camera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraActive(true);
        setError(null);
        startScanning();
      }
    } catch (err) {
      console.error('Camera error:', err);
      setError('Unable to access camera. Please check permissions.');
      setCameraActive(false);
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setScanning(false);
  };

  // Switch camera
  const switchCamera = () => {
    stopCamera();
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  };

  // Start scanning loop
  const startScanning = () => {
    setScanning(true);
    // In production, integrate with a scanning library here
    // For demo, we'll simulate with periodic checks
  };

  // Handle successful scan
  const handleScan = async (code: string) => {
    if (!code) return;
    
    setScanning(false);
    
    try {
      // Try to find asset by asset tag or QR code content
      const response = await api.get(`/assets/lookup?code=${encodeURIComponent(code)}`);
      
      if (response.data.data) {
        setScannedAsset(response.data.data);
        setError(null);
        toast({
          title: 'Asset Found',
          description: `Found: ${response.data.data.name}`,
        });
      } else {
        setError('No asset found with this code');
        setScannedAsset(null);
      }
    } catch (err) {
      setError('Failed to look up asset');
      setScannedAsset(null);
    }
  };

  // Manual search
  const handleManualSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualInput.trim()) return;
    
    await handleScan(manualInput.trim());
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  // Restart camera when facing mode changes
  useEffect(() => {
    if (cameraActive) {
      startCamera();
    }
  }, [facingMode]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Asset Scanner</h1>
        <p className="text-muted-foreground">Scan QR codes or barcodes for quick asset lookup</p>
      </div>

      {/* Scan Mode Tabs */}
      <div className="flex gap-2">
        <Button
          variant={scanMode === 'qr' ? 'default' : 'outline'}
          onClick={() => setScanMode('qr')}
        >
          <QrCode className="w-4 h-4 mr-2" />
          QR Code
        </Button>
        <Button
          variant={scanMode === 'barcode' ? 'default' : 'outline'}
          onClick={() => setScanMode('barcode')}
        >
          <Barcode className="w-4 h-4 mr-2" />
          Barcode
        </Button>
        <Button
          variant={scanMode === 'manual' ? 'default' : 'outline'}
          onClick={() => { setScanMode('manual'); stopCamera(); }}
        >
          <Search className="w-4 h-4 mr-2" />
          Manual
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Scanner Area */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {scanMode === 'manual' ? (
                <>
                  <Search className="w-5 h-5" />
                  Manual Lookup
                </>
              ) : (
                <>
                  <Camera className="w-5 h-5" />
                  {scanMode === 'qr' ? 'QR Code Scanner' : 'Barcode Scanner'}
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {scanMode === 'manual' ? (
              <form onSubmit={handleManualSearch} className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Enter Asset Tag, Serial Number, or QR Code
                  </label>
                  <Input
                    value={manualInput}
                    onChange={(e) => setManualInput(e.target.value)}
                    placeholder="e.g., AST-001 or SNLP202401001"
                    className="font-mono"
                  />
                </div>
                <Button type="submit" className="w-full">
                  <Search className="w-4 h-4 mr-2" />
                  Look Up Asset
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                {/* Camera View */}
                <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                  <video
                    ref={videoRef}
                    className={cn(
                      "w-full h-full object-cover",
                      !cameraActive && "hidden"
                    )}
                    playsInline
                    muted
                  />
                  <canvas ref={canvasRef} className="hidden" />
                  
                  {!cameraActive && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                      <Camera className="w-16 h-16 mb-4 opacity-50" />
                      <p className="text-sm opacity-75">Camera not active</p>
                    </div>
                  )}
                  
                  {cameraActive && scanning && (
                    <div className="absolute inset-0 pointer-events-none">
                      {/* Scan overlay */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className={cn(
                          "border-2 border-white/80 rounded-lg",
                          scanMode === 'qr' ? "w-48 h-48" : "w-64 h-24"
                        )}>
                          <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-primary rounded-tl-lg" />
                          <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-primary rounded-tr-lg" />
                          <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-primary rounded-bl-lg" />
                          <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-primary rounded-br-lg" />
                        </div>
                      </div>
                      {/* Scanning indicator */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/60 text-white px-3 py-1.5 rounded-full text-sm">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Scanning...
                      </div>
                    </div>
                  )}
                </div>

                {/* Camera Controls */}
                <div className="flex justify-center gap-2">
                  {!cameraActive ? (
                    <Button onClick={startCamera} className="flex-1">
                      <Camera className="w-4 h-4 mr-2" />
                      Start Camera
                    </Button>
                  ) : (
                    <>
                      <Button variant="outline" onClick={switchCamera}>
                        <SwitchCamera className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" onClick={() => setFlashOn(!flashOn)}>
                        <Flashlight className={cn("w-4 h-4", flashOn && "text-yellow-500")} />
                      </Button>
                      <Button variant="destructive" onClick={stopCamera}>
                        <X className="w-4 h-4 mr-2" />
                        Stop
                      </Button>
                    </>
                  )}
                </div>

                {/* Demo: Manual scan simulation */}
                <div className="pt-4 border-t">
                  <p className="text-sm text-muted-foreground mb-2">
                    Demo: Enter code manually to simulate scan
                  </p>
                  <form onSubmit={(e) => { e.preventDefault(); handleScan(manualInput); }} className="flex gap-2">
                    <Input
                      value={manualInput}
                      onChange={(e) => setManualInput(e.target.value)}
                      placeholder="Enter code to simulate scan"
                      className="flex-1"
                    />
                    <Button type="submit">Scan</Button>
                  </form>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Result Area */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {scannedAsset ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Asset Found
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  Scan Result
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {scannedAsset ? (
              <div className="space-y-4">
                {/* Asset Info */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Asset Tag</span>
                    <span className="font-mono font-medium text-primary">{scannedAsset.assetTag}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Name</span>
                    <span className="font-medium">{scannedAsset.name}</span>
                  </div>
                  {scannedAsset.serialNumber && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Serial Number</span>
                      <span className="font-mono">{scannedAsset.serialNumber}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Category</span>
                    <span>{scannedAsset.category?.name || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Brand</span>
                    <span>{scannedAsset.brand?.name || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Status</span>
                    <span className={cn(
                      "px-2 py-1 text-xs font-medium rounded-full",
                      scannedAsset.status === 'AVAILABLE' && "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
                      scannedAsset.status === 'ASSIGNED' && "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
                      scannedAsset.status === 'IN_MAINTENANCE' && "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
                      scannedAsset.status === 'RETIRED' && "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
                    )}>
                      {scannedAsset.status.replace('_', ' ')}
                    </span>
                  </div>
                  {scannedAsset.assignedTo && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Assigned To</span>
                      <span>{scannedAsset.assignedTo.firstName} {scannedAsset.assignedTo.lastName}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button 
                    className="flex-1" 
                    onClick={() => navigate(`/assets/${scannedAsset.id}`)}
                  >
                    View Details
                  </Button>
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => navigate(`/assets/${scannedAsset.id}/edit`)}
                  >
                    Edit Asset
                  </Button>
                </div>

                {/* Scan Another */}
                <Button 
                  variant="ghost" 
                  className="w-full"
                  onClick={() => {
                    setScannedAsset(null);
                    setManualInput('');
                    if (scanMode !== 'manual') {
                      startScanning();
                    }
                  }}
                >
                  Scan Another Asset
                </Button>
              </div>
            ) : (
              <div className="text-center py-8">
                <QrCode className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">
                  {scanMode === 'manual' 
                    ? 'Enter an asset tag or serial number to look up'
                    : 'Point your camera at a QR code or barcode to scan'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>How to Use</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                1
              </div>
              <div>
                <p className="font-medium">Select Scan Mode</p>
                <p className="text-sm text-muted-foreground">Choose QR code, barcode, or manual entry</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                2
              </div>
              <div>
                <p className="font-medium">Scan or Enter Code</p>
                <p className="text-sm text-muted-foreground">Point camera at code or type asset tag</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                3
              </div>
              <div>
                <p className="font-medium">View Asset Details</p>
                <p className="text-sm text-muted-foreground">Access full asset information instantly</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
